import React, { useEffect, useMemo, useRef } from "react";
import { applyListTemplate } from "src/helpers/applyTemplate";

export default function DocumentCustomCssPreview({ template, pageName, documents, onDocClick, className, autoResize = false, forceTailwind = false, tailwindCdn = "https://cdn.tailwindcss.com" }) {
  const iframeRef = useRef(null);

  const html = useMemo(() => {
    if (!template) return "";
    try {
      const safeDocs = Array.isArray(documents) ? documents : [];
      return applyListTemplate(template, pageName, safeDocs) || "";
    } catch {
      return "";
    }
  }, [template, pageName, documents]);

  useEffect(() => {
    const iframe = iframeRef.current;
    // if (!iframe || !html) return;

    const isFullDoc = /<\s*html[\s>]/i.test(html);
    const containsTailwind = (s) => /cdn\.tailwindcss\.com/i.test(s) || /tailwind(\.min)?\.css/i.test(s);

    const likelyUsesTWClasses = (s) => /\b(?:flex|grid|hidden|block|inline|p-\d|px-\d|py-\d|m-\d|rounded|shadow|text-[\w:/\-[\]]+|bg-[\w:/\-[\]]+)\b/.test(s);

    let finalHtml = html;

    if (isFullDoc) {
      if (!containsTailwind(html) && (forceTailwind || likelyUsesTWClasses(html))) {
        finalHtml = html.replace(
          /<\s*head(\s[^>]*)?>/i,
          (m) =>
            `${m}
  <script data-injected="tw" src="${tailwindCdn}"></script>`,
        );
      }
    } else {
      // fragment → wrap in shell
      const needsTW = forceTailwind || likelyUsesTWClasses(html);
      const twScript = needsTW ? `<script id="_tw-cdn" src="${tailwindCdn}"></script>` : "";
      const shell = `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width,initial-scale=1" />
  <base target="_blank" />
  ${twScript}
  <style>
    html, body { margin: 0; padding: 0; }
    img, video { max-width: 100%; height: auto; }
  </style>
</head>
<body>${html}</body>
</html>`;
      finalHtml = shell;
    }

    const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
    // if (!doc) return;

    doc?.open();
    doc?.write(finalHtml);
    doc?.close();

    // if fragment had Tailwind already, remove our fallback CDN
    if (!isFullDoc) {
      const hasUserTW = !!doc?.querySelector('script[src*="cdn.tailwindcss.com"]') || !!doc?.querySelector('link[href*="tailwind"]');
      if (hasUserTW) {
        const injected = doc?.getElementById("_tw-cdn");
        if (injected) injected.remove();
      }
    }

    // click handling
    const clickHandler = (e) => {
      let el = e.target;
      while (el && el !== doc?.body) {
        const id = el.getAttribute?.("data-doc-id");
        if (id) {
          e.preventDefault?.();
          onDocClick?.(id);
          return;
        }
        el = el.parentElement;
      }
    };
    doc?.addEventListener("click", clickHandler);

    let ro;
    const setHeight = () => {
      const h = Math.max(doc?.body.scrollHeight, doc?.documentElement.scrollHeight, doc?.body.offsetHeight, doc?.documentElement.offsetHeight, doc?.body.clientHeight, doc?.documentElement.clientHeight);
      iframe.style.height = `${h}px`;
    };

    if (autoResize) {
      setHeight();
      if (typeof ResizeObserver !== "undefined") {
        ro = new ResizeObserver(setHeight);
        ro.observe(doc?.body);
      } else {
        doc.defaultView?.addEventListener("resize", setHeight);
        setTimeout(setHeight, 0);
        setTimeout(setHeight, 250);
        setTimeout(setHeight, 1000);
      }
    }

    return () => {
      doc?.removeEventListener("click", clickHandler);
      if (ro) ro.disconnect();
      if (autoResize) doc?.defaultView?.removeEventListener("resize", setHeight);
    };
  }, [html, onDocClick, autoResize, forceTailwind, tailwindCdn]);

  return (
    <iframe
      ref={iframeRef}
      className={className}
      sandbox="allow-same-origin allow-scripts allow-popups allow-top-navigation-by-user-activation"
      srcDoc="<!doctype html><html><head></head><body></body></html>"
      style={{
        width: "100%",
        height: "100%",
        border: "none",
        backfaceVisibility: "hidden",
      }}
      title={pageName || "Custom Template"}
    />
  );
}
